// src/components/MemeChooserModal.tsx

import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { SIGNS, type ZodiacSignMemes } from '../utils/zodiacMemes';

interface Meme {
  memeId: string;
  imageUrl: string;
  description?: string;
  tone?: string;
}

interface MemeChooserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (selectedMeme: Meme) => void;
  memes: Meme[];
  dmLinks?: Partial<Record<string, string>>;
  userZodiacSign?: string | null;
}

const INK = '#1A1A1A';
const CARD_BG = '#2A1A12';
const WHITE = '#FFFFFF';
const CORAL = '#E05A3A';

const SHADOW_CARD = `6px 6px 0px ${INK}`;

const FONT_DISPLAY = "'Fredoka', sans-serif";
const FONT_BODY = "'Nunito', sans-serif";
const FONT_SYMBOL = "'Segoe UI Symbol', 'Noto Sans Symbols', 'Arial Unicode MS', sans-serif";

const FOOTER_INK = 'rgba(255, 210, 185, 0.92)';

const SHARE_TEXT = "Well it's not wrong... 👀";

function memeCardStyle(delayMs: number): React.CSSProperties {
  return {
    background: CARD_BG,
    border: `3px solid ${INK}`,
    borderRadius: 20,
    padding: '20px 16px 14px',
    position: 'relative',
    overflow: 'visible',
    boxShadow: SHADOW_CARD,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    opacity: 0,
    animation: 'cardIn 0.35s ease forwards',
    animationDelay: `${delayMs}ms`,
  };
}

function cardWatermarkStyle(signColor: string): React.CSSProperties {
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -60%)',
    fontSize: 160,
    lineHeight: 1,
    color: signColor,
    opacity: 0.16,
    fontFamily: FONT_SYMBOL,
    pointerEvents: 'none',
    userSelect: 'none',
    zIndex: 0,
  };
}

const stickerStyle: React.CSSProperties = {
  position: 'absolute',
  right: -16,
  bottom: -20,
  width: 140,
  height: 'auto',
  pointerEvents: 'none',
  userSelect: 'none',
  filter: 'drop-shadow(3px 3px 0 rgba(0,0,0,0.55))',
  transform: 'rotate(6deg)',
  zIndex: 2,
};

const postBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: CORAL,
  color: '#FFFFFF',
  border: `2.5px solid ${INK}`,
  boxShadow: `4px 4px 0px ${INK}`,
  borderRadius: 100,
  padding: '7px 18px',
  fontFamily: FONT_DISPLAY,
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  flexShrink: 0,
  lineHeight: 1,
};

function renderPunchline(text: string) {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((part, i) =>
    part.startsWith('*') && part.endsWith('*') ? (
      <em
        key={i}
        style={{
          color: 'rgba(255,255,255,0.45)',
          fontStyle: 'italic',
          fontSize: '0.9em',
        }}
      >
        {part.slice(1, -1)}
      </em>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export function MemeChooserModal({ isOpen, onClose, userZodiacSign }: MemeChooserModalProps) {
  const [showAll, setShowAll] = useState(false);
  const [sharingIdx, setSharingIdx] = useState<number | null>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const postBtnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (isOpen) {
      setShowAll(false);
      setSharingIdx(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const matchedIdx = userZodiacSign
    ? SIGNS.findIndex((s) => s.name.toLowerCase() === userZodiacSign.toLowerCase())
    : -1;
  const active: ZodiacSignMemes = SIGNS[matchedIdx >= 0 ? matchedIdx : 0];
  const visibleMemes = showAll ? active.memes : active.memes.slice(0, 3);
  const hasMoreMemes = active.memes.length > 3;

  const handleShare = async (idx: number) => {
    const cardEl = cardRefs.current[idx];
    if (!cardEl || sharingIdx !== null) return;
    setSharingIdx(idx);

    let blob: Blob | null = null;
    const buttonEl = postBtnRefs.current[idx];
    try {
      if (buttonEl) buttonEl.style.visibility = 'hidden';
      let canvas: HTMLCanvasElement;
      try {
        canvas = await html2canvas(cardEl, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: CARD_BG,
          onclone: async (_doc, el) => {
            const link = _doc.createElement('link');
            link.rel = 'stylesheet';
            link.href =
              'https://fonts.googleapis.com/css2?family=Fredoka:wght@700&family=Nunito:wght@600;700;800&display=swap';
            _doc.head.appendChild(link);
            await new Promise((res) => setTimeout(res, 500));

            const sticker = el.querySelector("img[alt*='Zodiyak']") as HTMLElement | null;
            if (sticker) {
              sticker.style.right = '0px';
              sticker.style.bottom = '0px';
            }
          },
        });
      } finally {
        if (buttonEl) buttonEl.style.visibility = 'visible';
      }
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );
      if (!blob) throw new Error('Failed to create image blob');

      const file = new File([blob], 'crushhh.png', { type: 'image/png' });

      const shareData = { files: [file], text: SHARE_TEXT };
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare(shareData) &&
        typeof navigator.share === 'function'
      ) {
        try {
          await navigator.share(shareData);
          return;
        } catch {
          // fall through to download
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crushhh.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      if (blob) {
        try {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'crushhh.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } catch {
          // give up
        }
      }
    } finally {
      setSharingIdx(null);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 16,
    overflowY: 'auto',
  };

  const cardStyle: React.CSSProperties = {
    position: 'relative',
    background: '#FFFFFF',
    border: `3px solid ${INK}`,
    borderRadius: 20,
    boxShadow: `6px 6px 0px ${INK}`,
    padding: '24px 20px',
    maxWidth: 480,
    width: '100%',
    margin: '24px auto',
    boxSizing: 'border-box',
  };

  const closeBtnStyle: React.CSSProperties = {
    position: 'absolute',
    top: 12,
    right: 16,
    fontFamily: FONT_DISPLAY,
    fontSize: 22,
    color: INK,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: FONT_DISPLAY,
    fontSize: 24,
    fontWeight: 700,
    color: INK,
    margin: 0,
    marginBottom: 4,
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: FONT_BODY,
    fontSize: 14,
    color: CORAL,
    fontWeight: 600,
    margin: 0,
    marginBottom: 20,
    textAlign: 'center',
  };

  const memeGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 14,
    width: '100%',
    padding: '8px 8px 16px 8px',
    boxSizing: 'border-box',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  };

  const notNowBtnStyle: React.CSSProperties = {
    background: 'transparent',
    color: INK,
    border: `3px solid ${INK}`,
    borderRadius: 100,
    padding: '12px 24px',
    fontFamily: FONT_DISPLAY,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    flex: 1,
    maxWidth: 180,
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeBtnStyle} onClick={onClose} aria-label="Close">
          ×
        </button>

        <h2 style={titleStyle}>Boost your chances</h2>
        <p style={subtitleStyle}>Post a fresh Crushhh hint</p>

        {/* Cards */}
        <div key={active.name}>
          <div style={memeGridStyle}>
            {visibleMemes.map((m, i) => (
              <div
                key={`${active.name}-${m.n}`}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                style={memeCardStyle(i * 75)}
              >
                <div style={cardWatermarkStyle(active.color)} aria-hidden="true">
                  {active.symbol}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      fontFamily: FONT_BODY,
                      fontSize: 13,
                      fontWeight: 800,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.75)',
                      lineHeight: 1.4,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Tell me without telling me...
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: 48,
                    fontWeight: 700,
                    color: active.color,
                    textAlign: 'center',
                    lineHeight: 1.1,
                    marginBottom: 12,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {active.name}
                </div>
                <div
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.55)',
                    letterSpacing: '0.04em',
                    marginBottom: 6,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {m.setup}
                </div>
                <div
                  style={{
                    color: 'rgba(255,255,255,0.2)',
                    fontSize: 18,
                    marginBottom: 10,
                    letterSpacing: '0.1em',
                  }}
                >
                  — — —
                </div>
                <div
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 16.5,
                    fontWeight: 700,
                    lineHeight: 1.55,
                    color: WHITE,
                    flexGrow: 1,
                    marginBottom: 18,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {renderPunchline(m.punchline)}
                </div>
                <div
                  style={{
                    height: 1,
                    background: 'rgba(255,255,255,0.1)',
                    marginBottom: 13,
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontFamily: FONT_BODY,
                      fontStyle: 'italic',
                      fontSize: 16,
                      fontWeight: 600,
                      color: FOOTER_INK,
                    }}
                  >
                    Somebody needs a hint.
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      ref={(el) => {
                        postBtnRefs.current[i] = el;
                      }}
                      onClick={() => handleShare(i)}
                      disabled={sharingIdx !== null}
                      style={{
                        ...postBtnStyle,
                        opacity: sharingIdx !== null && sharingIdx !== i ? 0.5 : 1,
                        cursor: sharingIdx !== null ? 'not-allowed' : 'pointer',
                      }}
                      aria-label="Share this card"
                    >
                      <span>{sharingIdx === i ? 'Saving...' : 'Post'}</span>
                      <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" />
                          <circle cx="6" cy="12" r="3" fill="currentColor" stroke="none" />
                          <circle cx="18" cy="19" r="3" fill="currentColor" stroke="none" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </span>
                    </button>
                    <span
                      style={{
                        fontFamily: FONT_BODY,
                        fontSize: 14,
                        fontWeight: 700,
                        color: FOOTER_INK,
                      }}
                    >
                      Crushhh.com
                    </span>
                  </div>
                </div>
                <img
                  src={active.image}
                  alt={`${active.name} Zodiyak mascot`}
                  draggable={false}
                  style={stickerStyle}
                />
              </div>
            ))}
          </div>

          {hasMoreMemes && (
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 16,
                fontWeight: 600,
                color: CORAL,
                background: 'transparent',
                border: `2px solid ${CORAL}`,
                borderRadius: 100,
                padding: '10px 28px',
                boxShadow: `3px 3px 0px ${INK}`,
                display: 'block',
                margin: '8px auto 16px',
                cursor: 'pointer',
              }}
            >
              {showAll ? 'Show less' : 'See more'}
            </button>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={notNowBtnStyle}>
            Not Now
          </button>
        </div>
      </div>
    </div>
  );
}
